import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Select, Input, InputAdornment, MenuItem, ListItemIcon, ListItemText, Checkbox, Chip } from '@mui/material';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import StyledBadge from 'components/UI/Badge';
import { BorderRadius } from 'styles/themeBorderRadius';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { Height } from 'styles/themeHeights';
import { Space } from 'styles/themeSpaces';
import { Color, FontSize, Size } from 'lib/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from './InfoIcon';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';


const SelectElement = muiStyled(Select)<any>(({ $error, $noBorder, $noBoxShadow, width, height, disabled, disabledColor }) => ({
  borderColor: $error ? Main.error : Border.main,
  width: width ?? '100%',
  height: height ?? Height.m,
  background: Background.white,
  fontSize: Space.sp2x,
  borderRadius: BorderRadius.m,
  boxSizing: 'border-box',
  border: $noBorder ? 'none' : `1px solid ${Border.main}`,
  boxShadow: $noBoxShadow ? 'none' : undefined,
  cursor: 'pointer !important',
  ...(disabled && {
    cursor: 'not-allowed',
    background: disabledColor ? `${disabledColor} !important` : 'transparent !important',
    opacity: !disabledColor ? 0.6 : undefined,
  }),
  '& .MuiSelect-select': {
    paddingLeft: '8px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer !important',
  },
  '& svg.MuiSvgIcon-root.MuiSelect-icon': {
    color: `${Text.primary} !important`,
  },
}));

const StyledListItemText = muiStyled(ListItemText)<any>({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
});

const OptionElement = muiStyled(MenuItem)({
  background: Background.white,
  display: 'flex',
  lineHeight: Height.xsPlus,
  fontSize: Space.sp2x,
  padding: '8px 16px !important',
  '& .MuiCheckbox-root': {
    color: '#FA8334',
  },
});

const SelectedValue = styled.span`
  padding: 3px;
  text-align: center;
`;

const PlaceHolder = styled.span`
  padding: 3px;
  color: #71777C;
`;

const ListItem = muiStyled(ListItemIcon)<any>({
  width: '15%',
  alignItems: 'center',
});

const Description = styled.span<any>`
  color: ${Text.placeholder};
  line-height: ${Height.xsPlus};
  word-wrap: break-word;
  white-space: normal;
  font-size: ${Space.spBasePlus};
  margin-top: ${Space.spQuarter};
  display: block;
  font-style: italic;
`;

const Label = styled.label<any>`
  color: ${Text.primary};
  font-size: ${Space.sp2x};
  line-height: ${Height.xsPlus};
  margin-bottom: ${({ $labelPosition, separation }) => $labelPosition === 'top' ? separation : '0px'};
  margin-right: ${({ $labelPosition }) => $labelPosition === 'left' ? '4px' : '0px'};
  margin-top: ${({ separation }) => separation === '8px' ? '0px' : '4px'};

    ${({ disabled }) => disabled && css`
      color: ${Color.darkGray};
    `}
`;

const ErrorLabel = styled.label<any>`
  color: #C82135;
  font-size: 14px;
  line-height: ${Height.xsPlus};
  margin-bottom: ${({ $labelPosition }) => $labelPosition === 'top' ? '8px' : '0px'};
  margin-right: ${({ $labelPosition }) => $labelPosition === 'left' ? '4px' : '0px'};
  margin-top: -15px;
  `;

const ErrorLabelBottom = styled.label<any>`
  color: #C82135;
  font-size: 12px;
  line-height: ${Height.xs};
  margin-top: 2px;
  `;

export const SelectContainer = styled(FlexboxContainer) <any>`
  padding-left: inherit;
  width: ${({ width }) => width ?? '100%'};
  cursor: pointer;

  .MuiSelect-select {
    &:focus {
      background-color: transparent !important;
    
    }
  }
`;

const IconAsterisk = styled.span`
  color: red;
  width: 5px;
  height: 5px;
  margin-left: 5px;
`;


const OptionalLabel = styled.span`
  font-size: ${FontSize.semi};
  color: grey;
  font-style: italic;
  margin-left: 5px;
`;

const StyledInfoIcon = styled(InfoIcon) <any>`
  display: flex;
  margin-left: ${Size.small};
`;

SelectElement.displayName = 'SelectElement';
OptionElement.displayName = 'OptionElement';

interface OptionBadge {
  content: string;
  color: string;
  backgroundColor: string;
}

export interface Option {
  value: string;
  label?: ReactNode;
  id?: string;
  code?: string;
  icon?: ReactNode;
  badge?: OptionBadge;
  disabled?: boolean;
}

interface Props {
  optionClassName?: string;
  value?: string | string[];
  defaultValue?: string;
  onChange: (event: any) => void;
  width?: string;
  containerWidth?: string;
  height?: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  description?: string;
  options: Option[];
  disabled?: boolean;
  selectIcon?: ReactNode;
  placeholder?: string;
  noBorder?: boolean;
  noBoxShadow?: boolean;
  isInvalid?: boolean;
  alignItems?: string;
  displayAsterisk?: boolean;
  displayEmpty?: string;
  multiple?: boolean;
  className?: string;
  isRedLable?: boolean;
  separation?: string;
  errroLabelPostion?: 'top' | 'bottom';
  limitTags?: number;
  errorMessage?: string;
  isOptional?: boolean;
  infoText?: string;
  padding?: string;
  disabledColor?: string;
  readOnly?: boolean;
  id?: string;
}

const StyledChip = muiStyled(Chip)({
  margin: '2px',
  height: '24px',
  maxWidth: '170px',
  backgroundColor: Main.brandDark,

  '& .MuiChip-label': {
    color: '#ffffff',
    WebkitTextFillColor: '#ffffff',
  },

  '& .MuiChip-deleteIcon': {
    color: '#ffffff',
  },
});

const menuPaperStyles = {
  maxHeight: 250,
  marginTop: '2px',
};

const SelectComponent = ({
  optionClassName,
  value,
  defaultValue,
  onChange,
  width,
  containerWidth,
  height,
  label,
  labelPosition,
  description,
  options,
  disabled,
  selectIcon,
  noBorder,
  noBoxShadow,
  isInvalid,
  alignItems = 'stretch',
  displayAsterisk = false,
  displayEmpty,
  multiple = false,
  className,
  isRedLable,
  separation = '8px',
  errroLabelPostion = 'top',
  limitTags = 3,
  errorMessage = 'Required',
  isOptional = false,
  infoText,
  padding = '0px',
  disabledColor,
  readOnly = false,
  placeholder = 'Select an option',
  id
}: Props) => {
  const [dirty, setDirty] = useState(false);
  const [selectOptionWidth, setSelectOptionWidth] = useState(0);
  const selectContainerRef = useRef<HTMLInputElement | null>(null);

  const selectMenuProps = {
    PaperProps: {
      sx: menuPaperStyles,
    },
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
    style: {
      maxWidth: `${selectOptionWidth || 0}px`
    },
    MenuListProps: {
      style: {
        marginRight: -25,
        width: '100%',
        maxHeight: 250,
        overflowY: 'auto' as const,
      }
    }
  };

  useEffect(() => {
    const optionWidth = selectContainerRef?.current?.offsetWidth;
    if (optionWidth) {
      setSelectOptionWidth(optionWidth);
    }
  }, []);

  const onChangeHandler = (event: any, element: any) => {
    const { id, value } = element?.props;

    if (id) {
      event.target.id = id;
    }

    const selectedOption = options.find((opt: any) => opt.value === value);
    if (selectedOption?.code) {
      event.target.code = selectedOption.code;
    }

    if (multiple) {
      event.target.selected = value;
    }

    if (!dirty) {
      setDirty(true);
    }

    if (value === 'All' && multiple) {
      // Toggle all options
      event.target.value =
        value.length === options.length ? [] : options.map((opt) => opt.value);
    }

    onChange(event);
  };

  const labelElement = label && (
    <FlexboxContainer>
      <Label disabled={disabled} $labelPosition={labelPosition} className={isRedLable && 'red-label'} separation={separation}>
        {label}
        {isOptional && <OptionalLabel>(optional)</OptionalLabel>}
      </Label>
      {displayAsterisk ? <IconAsterisk> * </IconAsterisk> : null}
    </FlexboxContainer>
  );

  const startIcon = selectIcon && (
    <InputAdornment position="start">
      {selectIcon}
    </InputAdornment>
  );

  const input = (
    <Input
      disableUnderline
      startAdornment={startIcon}
    />
  );


  const handleChipDelete = (chipToDelete: string, id: any) => {
    if (Array.isArray(value)) {
      const newValue = value.filter((chip: string) => chip !== chipToDelete);
      onChange({ target: { value: newValue, selected: chipToDelete, id } });
    } else {
      console.warn("Attempted to remove a chip in single-select mode, which is not allowed.");
    }
  };


  const renderSelectedValue = (value: any) => {
    // Check if value is empty (null, undefined, empty string or empty array)
    const isEmptyValue = value === null || value === undefined || value === '' ||
      (multiple && Array.isArray(value) && value.length === 0);

    // Show placeholder when value is empty
    if (isEmptyValue) {
      return (
        <PlaceHolder>
          <em>{placeholder}</em>
        </PlaceHolder>
      );
    }

    // If there's a defaultValue and the current value matches it
    if (defaultValue && value === defaultValue) {
      return (
        <PlaceHolder>
          <em>{defaultValue}</em>
        </PlaceHolder>
      );
    }

    const filteredValue = Array.isArray(value) ? value.filter((x: string) => x !== 'All') : value;

    const limitedValues = multiple && filteredValue.length > 0 && limitTags ? filteredValue.slice(0, limitTags) : filteredValue;
    const remainingCount = multiple && filteredValue.length > limitTags ? filteredValue.length - limitTags : 0;

    return (
      <SelectedValue>
        {multiple ? (
          <>
            {limitedValues.map((val: any) => (
              <StyledChip
                key={val}
                label={val}
                disabled={disabled}
                onDelete={(event) => {
                  event.stopPropagation(); // Ensure delete logic prevents dropdown interaction
                  handleChipDelete(val, event.target.id); // Call the delete handler
                }}
                deleteIcon={<CloseIcon style={{ color: Main.white }} />}
                onMouseDown={(event) => {
                  event.stopPropagation(); // Prevent the select dropdown from opening
                }}
              />
            ))}
            {remainingCount > 0 && (
              <span
                // label={`+${remainingCount} more`}
                // className={classes.chip}
                style={{
                  // backgroundColor: Main.brandDark,
                  color: Main.brandDark,
                  padding: 5,
                  fontSize: 14,
                }}
              >
                +{remainingCount} more
              </span>
            )}
          </>
        ) : (
          options.find((opt) => opt.value === value)?.label ?? value
        )}
      </SelectedValue>
    );
  };


  const enhancedOptions = displayEmpty ? [{ value: displayEmpty, id: '' }, ...options] : options;
  return (
    <FlexboxContainer
      displayFlex="flex"
      flexWrap="wrap"
      width={containerWidth || '100%'}
      alignItems={alignItems}
      padding={padding}
    >
      {
        labelPosition === 'left' ?
          (
            <FlexboxContainer>
              {labelPosition && labelElement}
            </FlexboxContainer>
          ) : null
      }
      <SelectContainer
        width={width}
        flexDirection="column"
      >
        <FlexboxContainer flexDirection='row'>
          {labelPosition === 'top' && labelElement}
          {infoText && <StyledInfoIcon tooltipText={infoText} productRedesignFlag />}
        </FlexboxContainer>
        <ReadOnlyWrapper readOnly={readOnly}>
          <SelectElement
            id={id}
            $noBorder={noBorder}
            $noBoxShadow={noBoxShadow}
            $error={isInvalid}
            defaultValue={null}
            input={input}
            value={value}
            onChange={onChangeHandler}
            disabled={disabled || readOnly}
            MenuProps={selectMenuProps}
            renderValue={renderSelectedValue}
            height={height}
            displayEmpty
            ref={selectContainerRef}
            multiple={multiple}
            className={className}
            disabledColor={disabledColor}
            placeholder={placeholder}
          >
            {
              enhancedOptions.map((option: any, key) => (
                <OptionElement
                  key={key}
                  value={option.value}
                  id={option?.id}
                  className={optionClassName}
                  disabled={option.disabled}
                >
                  {multiple && <>
                    <StyledListItemText disableTypography={true} primary={option.label ?? option.value} />
                    <Checkbox checked={value && value.length > 0 ? value.indexOf(option.value) > -1 : false} />
                  </>}
                  {!multiple && <FlexboxContainer
                    flexDirection="row"
                    justifyContent="space-between"
                    width="100%"
                  >
                    {
                      option.icon && (
                        <ListItem marginThreshold={0}>
                          {option.icon}
                        </ListItem>
                      )
                    }
                    <FlexboxContainer
                      flexDirection="column"
                      width="100%"
                    >
                      {!multiple && <StyledListItemText disableTypography={true} primary={option.label ?? option.value} />}
                      {option.description && <Description>{option.description}</Description>}
                    </FlexboxContainer>
                    {
                      option.badge && (
                        <ListItem>
                          <StyledBadge
                            badgeContent={option.badge.content}
                            color={option.badge.color}
                            bgColor={option.badge.backgroundColor}
                            classes={{ badge: 'badge' }}
                          />
                        </ListItem>
                      )
                    }
                  </FlexboxContainer>}
                </OptionElement>
              ))
            }
          </SelectElement>
        </ReadOnlyWrapper>
        {errroLabelPostion === 'bottom' && isInvalid && <ErrorLabelBottom>{errorMessage}</ErrorLabelBottom>}
      </SelectContainer>
      {errroLabelPostion === 'top' && isInvalid && <ErrorLabel>{errorMessage}</ErrorLabel>}
    </FlexboxContainer>
  );
};

export default SelectComponent;
